/**
 * Robot Diagnostics Dashboard
 *
 * Main dashboard for viewing and running robot response diagnostics.
 *
 * Story 55: Diagnose robot response issues for a site
 */

import { useState, useEffect, useCallback } from 'react';
import { useSite } from '@/contexts/SiteContext';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Loader2,
  Play,
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Bot,
  Clock,
  ExternalLink,
  ChevronDown,
  ChevronUp,
  Sparkles,
  X,
  Search,
  Package,
} from 'lucide-react';
import {
  runDiagnostic,
  getLatestDiagnostic,
  getDiagnosticHistory,
  checkCooldown,
  analyzeWithAI,
  testProductVisibility,
  DiagnosticRun,
  DiagnosticCheck,
  DiagnosticHistory,
  AIAnalysisResult,
  ProductVisibilityResult,
  getSeverityColor,
  getSeverityBadgeColor,
  getHealthScoreColor,
  getHealthScoreBackground,
  formatCheckType,
} from '@/services/robotDiagnosticsService';
import { Input } from '@/components/ui/input';
import { AnswerRenderer } from '@/components/AnswerRenderer';
import { formatDistanceToNow, format } from 'date-fns';
import { cleanProductUrl } from '@/utils/url-cleaner';
import { SitemapHealthCard } from './SitemapHealthCard';

// Brand icons as inline SVG components
const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" className="h-8 w-8">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

const BingIcon = () => (
  <svg viewBox="0 0 24 24" className="h-8 w-8">
    <path fill="#00897B" d="M5 3v16.5l4 2.5v-6l6 3.5-6-10V3z"/>
    <path fill="#00ACC1" d="M9 16v3l6 3.5-6-3.5z"/>
    <path fill="#4DD0E1" d="M9 6v10l6 3.5V9.5z"/>
  </svg>
);

const OpenAIIcon = () => (
  <svg viewBox="0 0 24 24" className="h-8 w-8">
    <path fill="#10A37F" d="M22.2819 9.8211a5.9847 5.9847 0 0 0-.5157-4.9108 6.0462 6.0462 0 0 0-6.5098-2.9A6.0651 6.0651 0 0 0 4.9807 4.1818a5.9847 5.9847 0 0 0-3.9977 2.9 6.0462 6.0462 0 0 0 .7427 7.0966 5.98 5.98 0 0 0 .511 4.9107 6.051 6.051 0 0 0 6.5146 2.9001A5.9847 5.9847 0 0 0 13.2599 24a6.0557 6.0557 0 0 0 5.7718-4.2058 5.9894 5.9894 0 0 0 3.9977-2.9001 6.0557 6.0557 0 0 0-.7475-7.0729zm-9.022 12.6081a4.4755 4.4755 0 0 1-2.8764-1.0408l.1419-.0804 4.7783-2.7582a.7948.7948 0 0 0 .3927-.6813v-6.7369l2.02 1.1686a.071.071 0 0 1 .038.052v5.5826a4.504 4.504 0 0 1-4.4945 4.4944zm-9.6607-4.1254a4.4708 4.4708 0 0 1-.5346-3.0137l.142.0852 4.783 2.7582a.7712.7712 0 0 0 .7806 0l5.8428-3.3685v2.3324a.0804.0804 0 0 1-.0332.0615L9.74 19.9502a4.4992 4.4992 0 0 1-6.1408-1.6464zM2.3408 7.8956a4.485 4.485 0 0 1 2.3655-1.9728V11.6a.7664.7664 0 0 0 .3879.6765l5.8144 3.3543-2.0201 1.1685a.0757.0757 0 0 1-.071 0l-4.8303-2.7865A4.504 4.504 0 0 1 2.3408 7.8956zm16.0993 3.8558L12.6 8.3829l2.02-1.1638a.0757.0757 0 0 1 .071 0l4.8303 2.7913a4.4944 4.4944 0 0 1-.6765 8.1042v-5.6772a.79.79 0 0 0-.407-.667zm2.0107-3.0231l-.142-.0852-4.7735-2.7818a.7759.7759 0 0 0-.7854 0L9.409 9.2297V6.8974a.0662.0662 0 0 1 .0284-.0615l4.8303-2.7866a4.4992 4.4992 0 0 1 6.6802 4.66zM8.3065 12.863l-2.02-1.1638a.0804.0804 0 0 1-.038-.0567V6.0742a4.4992 4.4992 0 0 1 7.3757-3.4537l-.142.0805L8.704 5.459a.7948.7948 0 0 0-.3927.6813zm1.0976-2.3654l2.602-1.4998 2.6069 1.4998v2.9994l-2.5974 1.5056-2.6067-1.5056z"/>
  </svg>
);

// Helper to get bot status from checks
function getBotStatus(checks: DiagnosticCheck[] | undefined, botName: string): {
  status: 'allowed' | 'blocked' | 'partial';
  issues: string[];
} {
  if (!checks || checks.length === 0) {
    return { status: 'allowed', issues: [] };
  }

  const botChecks = checks.filter(c =>
    c.user_agent?.toLowerCase().includes(botName.toLowerCase()) ||
    (c.check_type === 'robots_txt' && c.title.toLowerCase().includes(botName.toLowerCase()))
  );

  const criticalIssues = botChecks.filter(c => c.severity === 'critical');
  const warningIssues = botChecks.filter(c => c.severity === 'warning');

  if (criticalIssues.length > 0) {
    return {
      status: 'blocked',
      issues: criticalIssues.map(c => c.title)
    };
  }

  if (warningIssues.length > 0) {
    return {
      status: 'partial',
      issues: warningIssues.map(c => c.title)
    };
  }

  return { status: 'allowed', issues: [] };
}

export default function RobotDiagnosticsDashboard() {
  const { selectedSite } = useSite();
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [latestRun, setLatestRun] = useState<DiagnosticRun | null>(null);
  const [history, setHistory] = useState<DiagnosticHistory[]>([]);
  const [cooldown, setCooldown] = useState<{ allowed: boolean; remainingMinutes?: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [expandedChecks, setExpandedChecks] = useState<Set<string>>(new Set());

  // AI Analysis state
  const [analyzingAI, setAnalyzingAI] = useState(false);
  const [aiAnalysis, setAiAnalysis] = useState<AIAnalysisResult | null>(null);
  // Show panel if there's a saved analysis or user requests new one
  const [showAIPanel, setShowAIPanel] = useState(false);
  const [aiPanelExpanded, setAiPanelExpanded] = useState(true);

  // Product visibility test state
  const [productQuery, setProductQuery] = useState('');
  const [testingProduct, setTestingProduct] = useState(false);
  const [productResult, setProductResult] = useState<ProductVisibilityResult | null>(null);
  const [showProductPanel, setShowProductPanel] = useState(false);

  const loadData = useCallback(async () => {
    if (!selectedSite?.id) return;

    setLoading(true);
    setError(null);

    try {
      const [latestResult, historyResult, cooldownResult] = await Promise.all([
        getLatestDiagnostic(selectedSite.id),
        getDiagnosticHistory(selectedSite.id, 30),
        checkCooldown(selectedSite.id),
      ]);

      if ('hasRun' in latestResult && !latestResult.hasRun) {
        setLatestRun(null);
      } else {
        setLatestRun(latestResult as DiagnosticRun);
      }

      setHistory(historyResult.history);
      setCooldown(cooldownResult);
    } catch (err) {
      console.error('Failed to load diagnostic data:', err);
      setError('Failed to load diagnostic data');
    } finally {
      setLoading(false);
    }
  }, [selectedSite?.id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleRunDiagnostic = async () => {
    if (!selectedSite?.id) return;

    setRunning(true);
    setError(null);

    try {
      const result = await runDiagnostic(selectedSite.id);

      if (!result.success) {
        if (result.error === 'cooldown') {
          setError(result.message || 'Please wait before running another diagnostic');
          setCooldown({ allowed: false, remainingMinutes: parseInt(result.message?.match(/\d+/)?.[0] || '60') });
        } else {
          setError(result.message || 'Failed to run diagnostic');
        }
        return;
      }

      // Reload data to get the new run
      await loadData();
    } catch (err) {
      console.error('Failed to run diagnostic:', err);
      setError('Failed to run diagnostic');
    } finally {
      setRunning(false);
    }
  };

  const toggleCheck = (checkId: string) => {
    setExpandedChecks(prev => {
      const next = new Set(prev);
      if (next.has(checkId)) {
        next.delete(checkId);
      } else {
        next.add(checkId);
      }
      return next;
    });
  };

  const handleAskAI = async () => {
    if (!selectedSite?.id) return;

    setAnalyzingAI(true);
    setError(null);
    setShowAIPanel(true);
    setAiPanelExpanded(true);

    try {
      const result = await analyzeWithAI(selectedSite.id, latestRun?.id);
      setAiAnalysis(result);

      // Update the latestRun with the saved analysis
      if (latestRun && result.analysis) {
        setLatestRun({
          ...latestRun,
          ai_analysis: result.analysis,
          ai_analysis_at: new Date().toISOString()
        });
      }
    } catch (err) {
      console.error('Failed to get AI analysis:', err);
      setError('Failed to get AI analysis');
    } finally {
      setAnalyzingAI(false);
    }
  };

  const handleTestProduct = async () => {
    if (!selectedSite?.id || !productQuery.trim()) return;

    setTestingProduct(true);
    setError(null);
    setShowProductPanel(true);
    setProductResult(null);

    try {
      const result = await testProductVisibility(selectedSite.id, productQuery);
      setProductResult(result);
    } catch (err) {
      console.error('Failed to test product visibility:', err);
      setError('Failed to test product visibility');
    } finally {
      setTestingProduct(false);
    }
  };

  if (!selectedSite) {
    return (
      <div className="container mx-auto p-6">
        <Alert>
          <AlertDescription>Please select a site to view robot diagnostics.</AlertDescription>
        </Alert>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="container mx-auto p-6 flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Bot className="h-6 w-6" />
            Robot Diagnostics
          </h1>
          <p className="text-muted-foreground mt-1">
            Check how search engine robots see your site
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={loadData} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button
            variant="outline"
            onClick={handleAskAI}
            disabled={analyzingAI}
          >
            {analyzingAI ? (
              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
            ) : (
              <Sparkles className="h-4 w-4 mr-1" />
            )}
            {analyzingAI ? 'Analyzing...' : 'Ask AI'}
          </Button>
          <Button
            onClick={handleRunDiagnostic}
            disabled={running || (cooldown && !cooldown.allowed)}
          >
            {running ? (
              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
            ) : (
              <Play className="h-4 w-4 mr-1" />
            )}
            {running ? 'Running...' : 'Run Diagnostic'}
          </Button>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* AI Analysis Panel - Show if panel is open OR if there's saved analysis */}
      {(showAIPanel || latestRun?.ai_analysis) && (
        <Card className="border-purple-200 dark:border-purple-900 bg-purple-50/50 dark:bg-purple-950/30">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-purple-600" />
                AI Analysis
                {latestRun?.ai_analysis_at && !analyzingAI && (
                  <span className="text-xs font-normal text-muted-foreground">
                    (analyzed {formatDistanceToNow(new Date(latestRun.ai_analysis_at), { addSuffix: true })})
                  </span>
                )}
              </CardTitle>
              <div className="flex items-center gap-1">
                {(latestRun?.ai_analysis || aiAnalysis?.analysis) && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleAskAI}
                    disabled={analyzingAI}
                    className="h-7 px-2 text-xs"
                  >
                    {analyzingAI ? (
                      <Loader2 className="h-3 w-3 animate-spin mr-1" />
                    ) : (
                      <RefreshCw className="h-3 w-3 mr-1" />
                    )}
                    Refresh
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setAiPanelExpanded(!aiPanelExpanded)}
                  className="h-6 w-6 p-0"
                >
                  {aiPanelExpanded ? (
                    <ChevronUp className="h-4 w-4" />
                  ) : (
                    <ChevronDown className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>
            <CardDescription>
              {analyzingAI
                ? 'Generating analysis...'
                : latestRun?.ai_analysis
                ? 'Recommendations based on robots.txt and diagnostic results'
                : 'Get AI-powered recommendations for your site'}
            </CardDescription>
          </CardHeader>
          {aiPanelExpanded && (
            <CardContent>
              {analyzingAI ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-purple-600 mr-2" />
                  <span className="text-muted-foreground">Analyzing your site configuration...</span>
                </div>
              ) : (aiAnalysis?.analysis || latestRun?.ai_analysis) ? (
                <div>
                  <AnswerRenderer answer={aiAnalysis?.analysis || latestRun?.ai_analysis || ''} />
                  {aiAnalysis?.cost && (
                    <div className="mt-4 pt-4 border-t text-xs text-muted-foreground">
                      Analysis cost: ${aiAnalysis.cost.totalCost.toFixed(4)}
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Button onClick={handleAskAI} disabled={analyzingAI} variant="outline">
                    <Sparkles className="h-4 w-4 mr-2" />
                    Generate AI Analysis
                  </Button>
                </div>
              )}
            </CardContent>
          )}
        </Card>
      )}

      {/* Product Visibility Test */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Package className="h-5 w-5" />
            Product Visibility Test
          </CardTitle>
          <CardDescription>
            Test if a specific product is visible to search engines and AI assistants
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2">
            <Input
              placeholder="Enter product name or URL..."
              value={productQuery}
              onChange={(e) => setProductQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleTestProduct()}
              className="flex-1"
            />
            <Button
              onClick={handleTestProduct}
              disabled={testingProduct || !productQuery.trim()}
            >
              {testingProduct ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Search className="h-4 w-4" />
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Product Visibility Results Panel */}
      {showProductPanel && (
        <Card className="border-blue-200 dark:border-blue-900 bg-blue-50/50 dark:bg-blue-950/30">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <Package className="h-5 w-5 text-blue-600" />
                Visibility Test Results
              </CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowProductPanel(false)}
                className="h-6 w-6 p-0"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            {productResult?.product && (
              <CardDescription>
                Testing: {productResult.product.name}
                {productResult.product.url && (
                  <a
                    href={cleanProductUrl(productResult.product.url)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ml-2 inline-flex items-center text-blue-600 hover:underline"
                  >
                    <ExternalLink className="h-3 w-3 mr-1" />
                    View
                  </a>
                )}
              </CardDescription>
            )}
          </CardHeader>
          <CardContent>
            {testingProduct ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-blue-600 mr-2" />
                <span className="text-muted-foreground">Testing product visibility...</span>
              </div>
            ) : productResult ? (
              <div className="space-y-4">
                {/* Overall Status */}
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">Overall Status:</span>
                  <Badge
                    className={
                      productResult.overall_status === 'visible'
                        ? 'bg-green-500 text-white'
                        : productResult.overall_status === 'blocked'
                        ? 'bg-red-500 text-white'
                        : productResult.overall_status === 'partial'
                        ? 'bg-amber-500 text-white'
                        : 'bg-gray-500 text-white'
                    }
                  >
                    {productResult.overall_status === 'visible'
                      ? 'Visible'
                      : productResult.overall_status === 'blocked'
                      ? 'Blocked'
                      : productResult.overall_status === 'partial'
                      ? 'Partially Visible'
                      : 'Unknown'}
                  </Badge>
                </div>

                {/* Technical Checks */}
                {productResult.checks && productResult.checks.length > 0 && (
                  <div className="space-y-2">
                    <div className="text-sm font-medium">Technical Checks:</div>
                    {productResult.checks.map((check, i) => (
                      <div
                        key={i}
                        className={`flex items-start gap-2 p-2 rounded text-sm ${
                          check.status === 'pass'
                            ? 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-200'
                            : check.status === 'fail'
                            ? 'bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-200'
                            : 'bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200'
                        }`}
                      >
                        {check.status === 'pass' ? (
                          <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                        ) : check.status === 'fail' ? (
                          <XCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                        ) : (
                          <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                        )}
                        <div>
                          <span className="font-medium">{check.name}:</span>{' '}
                          {check.message}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* AI Analysis */}
                {productResult.ai_visibility && (
                  <div className="space-y-2">
                    <div className="text-sm font-medium flex items-center gap-1">
                      <Sparkles className="h-4 w-4 text-purple-600" />
                      AI Visibility Analysis
                    </div>
                    <div className="bg-white dark:bg-slate-800 p-3 rounded border dark:border-slate-700">
                      <AnswerRenderer answer={productResult.ai_visibility.analysis} />
                    </div>
                    {productResult.ai_visibility.cost && (
                      <div className="text-xs text-muted-foreground">
                        Analysis cost: ${productResult.ai_visibility.cost.totalCost.toFixed(4)}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                Enter a product name or URL above and click search
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Sitemap Health Card */}
      <SitemapHealthCard siteId={selectedSite.id} />

      {/* Cooldown Warning */}
      {cooldown && !cooldown.allowed && (
        <Alert>
          <Clock className="h-4 w-4" />
          <AlertDescription>
            Next diagnostic available in {cooldown.remainingMinutes} minutes
          </AlertDescription>
        </Alert>
      )}

      {/* No Runs Yet */}
      {!latestRun && !running && (
        <Card>
          <CardContent className="py-12 text-center">
            <Bot className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">No diagnostics yet</h3>
            <p className="text-muted-foreground mb-4">
              Run your first diagnostic to see how search robots view your site
            </p>
            <Button onClick={handleRunDiagnostic} disabled={running}>
              <Play className="h-4 w-4 mr-1" />
              Run First Diagnostic
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Latest Run Summary */}
      {latestRun && (
        <>
          {/* Health Score Card */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="md:col-span-1">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Health Score
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-4">
                  <div
                    className={`text-4xl font-bold ${getHealthScoreColor(latestRun.health_score || 0)}`}
                  >
                    {latestRun.health_score ?? '--'}
                  </div>
                  <div className="flex-1">
                    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className={`h-full ${getHealthScoreBackground(latestRun.health_score || 0)}`}
                        style={{ width: `${latestRun.health_score || 0}%` }}
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1">
                  <XCircle className="h-4 w-4 text-red-500" />
                  Critical
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-red-600">
                  {latestRun.critical_count}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1">
                  <AlertTriangle className="h-4 w-4 text-amber-500" />
                  Warnings
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-amber-600">
                  {latestRun.warning_count}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  Passed
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-green-600">
                  {latestRun.passed_count}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Bot Access Status */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Bot Access Status</CardTitle>
              <CardDescription>How search engines and AI bots can access your site</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Googlebot */}
                {(() => {
                  const status = getBotStatus(latestRun.checks, 'googlebot');
                  return (
                    <div className={`p-4 rounded-lg border-2 ${
                      status.status === 'allowed' ? 'border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-950/40' :
                      status.status === 'blocked' ? 'border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/40' :
                      'border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/40'
                    }`}>
                      <div className="flex items-start justify-between mb-3">
                        <div className="p-2 bg-white dark:bg-slate-800 rounded-lg shadow-sm">
                          <GoogleIcon />
                        </div>
                        <Badge className={
                          status.status === 'allowed' ? 'bg-green-500 text-white' :
                          status.status === 'blocked' ? 'bg-red-500 text-white' :
                          'bg-amber-500 text-white'
                        }>
                          {status.status === 'allowed' ? 'Allowed' :
                           status.status === 'blocked' ? 'Blocked' : 'Partial'}
                        </Badge>
                      </div>
                      <div className="font-semibold">Googlebot</div>
                      <div className="text-sm text-muted-foreground">
                        {status.issues.length > 0 ? status.issues[0] : 'Full access to your site'}
                      </div>
                    </div>
                  );
                })()}

                {/* Bingbot */}
                {(() => {
                  const status = getBotStatus(latestRun.checks, 'bingbot');
                  return (
                    <div className={`p-4 rounded-lg border-2 ${
                      status.status === 'allowed' ? 'border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-950/40' :
                      status.status === 'blocked' ? 'border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/40' :
                      'border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/40'
                    }`}>
                      <div className="flex items-start justify-between mb-3">
                        <div className="p-2 bg-white dark:bg-slate-800 rounded-lg shadow-sm">
                          <BingIcon />
                        </div>
                        <Badge className={
                          status.status === 'allowed' ? 'bg-green-500 text-white' :
                          status.status === 'blocked' ? 'bg-red-500 text-white' :
                          'bg-amber-500 text-white'
                        }>
                          {status.status === 'allowed' ? 'Allowed' :
                           status.status === 'blocked' ? 'Blocked' : 'Partial'}
                        </Badge>
                      </div>
                      <div className="font-semibold">Bingbot</div>
                      <div className="text-sm text-muted-foreground">
                        {status.issues.length > 0 ? status.issues[0] : 'Full access to your site'}
                      </div>
                    </div>
                  );
                })()}

                {/* GPTBot */}
                {(() => {
                  const status = getBotStatus(latestRun.checks, 'gptbot');
                  return (
                    <div className={`p-4 rounded-lg border-2 ${
                      status.status === 'allowed' ? 'border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-950/40' :
                      status.status === 'blocked' ? 'border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/40' :
                      'border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/40'
                    }`}>
                      <div className="flex items-start justify-between mb-3">
                        <div className="p-2 bg-white dark:bg-slate-800 rounded-lg shadow-sm">
                          <OpenAIIcon />
                        </div>
                        <Badge className={
                          status.status === 'allowed' ? 'bg-green-500 text-white' :
                          status.status === 'blocked' ? 'bg-red-500 text-white' :
                          'bg-amber-500 text-white'
                        }>
                          {status.status === 'allowed' ? 'Allowed' :
                           status.status === 'blocked' ? 'Blocked' : 'Partial'}
                        </Badge>
                      </div>
                      <div className="font-semibold">GPTBot</div>
                      <div className="text-sm text-muted-foreground">
                        {status.issues.length > 0 ? status.issues[0] : 'Full access to your site'}
                      </div>
                    </div>
                  );
                })()}
              </div>
            </CardContent>
          </Card>

          {/* Last Run Info */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base">Latest Diagnostic Run</CardTitle>
                  <CardDescription>
                    {latestRun.completed_at
                      ? `Completed ${formatDistanceToNow(new Date(latestRun.completed_at), { addSuffix: true })}`
                      : 'Running...'}
                  </CardDescription>
                </div>
                <div className="text-sm text-muted-foreground">
                  {latestRun.urls_tested} URLs tested
                </div>
              </div>
            </CardHeader>
          </Card>

          {/* Issues List */}
          {latestRun.checks && latestRun.checks.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Issues Found</CardTitle>
                <CardDescription>
                  Click on an issue to see details and recommendations
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                {latestRun.checks
                  .filter(c => c.severity !== 'passed')
                  .map((check) => (
                    <div
                      key={check.id}
                      className={`border rounded-lg overflow-hidden ${getSeverityColor(check.severity)}`}
                    >
                      <button
                        className="w-full p-3 flex items-center justify-between text-left"
                        onClick={() => toggleCheck(check.id)}
                      >
                        <div className="flex items-center gap-3">
                          <Badge className={`${getSeverityBadgeColor(check.severity)} text-white`}>
                            {check.severity}
                          </Badge>
                          <div>
                            <div className="font-medium">{check.title}</div>
                            <div className="text-sm opacity-75">
                              {formatCheckType(check.check_type)}
                              {check.user_agent && ` - ${check.user_agent}`}
                            </div>
                          </div>
                        </div>
                        {expandedChecks.has(check.id) ? (
                          <ChevronUp className="h-4 w-4" />
                        ) : (
                          <ChevronDown className="h-4 w-4" />
                        )}
                      </button>

                      {expandedChecks.has(check.id) && (
                        <div className="px-3 pb-3 border-t border-current/10">
                          <div className="pt-3 space-y-3">
                            {/* URL */}
                            <div>
                              <div className="text-xs font-medium uppercase opacity-75 mb-1">URL</div>
                              <a
                                href={check.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-sm flex items-center gap-1 hover:underline"
                              >
                                {check.url.length > 60 ? check.url.substring(0, 60) + '...' : check.url}
                                <ExternalLink className="h-3 w-3" />
                              </a>
                            </div>

                            {/* Details */}
                            {Object.keys(check.details).length > 0 && (
                              <div>
                                <div className="text-xs font-medium uppercase opacity-75 mb-1">Details</div>
                                <pre className="text-xs bg-black/5 p-2 rounded overflow-x-auto">
                                  {JSON.stringify(check.details, null, 2)}
                                </pre>
                              </div>
                            )}

                            {/* Recommendations */}
                            {check.recommendations && check.recommendations.length > 0 && (
                              <div>
                                <div className="text-xs font-medium uppercase opacity-75 mb-1">
                                  Recommendations
                                </div>
                                <ul className="text-sm space-y-1">
                                  {check.recommendations.map((rec, i) => (
                                    <li key={i} className="flex items-start gap-2">
                                      <span className="text-green-600">*</span>
                                      {rec}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}

                {latestRun.checks.filter(c => c.severity !== 'passed').length === 0 && (
                  <div className="text-center py-8 text-muted-foreground">
                    <CheckCircle className="h-12 w-12 mx-auto text-green-500 mb-2" />
                    <p>No issues found! Your site looks good to search robots.</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* History */}
          {history.length > 1 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Diagnostic History</CardTitle>
                <CardDescription>Past 30 days</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {history.slice(0, 10).map((run) => (
                    <div
                      key={run.id}
                      className="flex items-center justify-between p-2 rounded border"
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className={`w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-bold ${getHealthScoreBackground(run.health_score || 0)}`}
                        >
                          {run.health_score ?? '--'}
                        </div>
                        <div>
                          <div className="text-sm font-medium">
                            {run.completed_at
                              ? format(new Date(run.completed_at), 'MMM d, yyyy HH:mm')
                              : 'Running...'}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {run.critical_count} critical, {run.warning_count} warnings, {run.passed_count} passed
                          </div>
                        </div>
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {run.urls_tested} URLs
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
